import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaBullseye, FaArrowRight } from 'react-icons/fa';

const mono = "'JetBrains Mono', 'Consolas', monospace";
const accent = '#00B4D8';

const Panel = styled.section`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.015), transparent 35%),
    var(--color-tertiary);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 1.1rem 1.15rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0 0 0.85rem;
  padding: 0 0 0.55rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg { color: ${accent}; }
`;

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  counter-reset: pt;
`;

const Item = styled.li`
  display: flex;
  gap: 0.7rem;
  padding: 0.55rem 0.7rem;
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  font-size: 0.85rem;
  line-height: 1.4;
  color: var(--text-secondary);
  counter-increment: pt;
  transition: border-color 0.18s ease, transform 0.18s ease, background 0.18s ease;

  &::before {
    content: counter(pt, decimal-leading-zero);
    font-family: ${mono};
    font-size: 0.7rem;
    color: ${accent};
    letter-spacing: 0.08em;
    flex-shrink: 0;
    min-width: 22px;
  }

  &:hover {
    border-color: rgba(0, 180, 216, 0.25);
    background: rgba(0, 180, 216, 0.04);
    transform: translateX(2px);
  }
`;

const CtaButton = styled.button`
  margin-top: 1rem;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 0.9rem;
  background: rgba(0, 180, 216, 0.08);
  color: ${accent};
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  font-family: inherit;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;

  svg { font-size: 0.7rem; transition: transform 0.18s ease; }

  &:hover {
    background: rgba(0, 180, 216, 0.16);
    border-color: rgba(0, 180, 216, 0.55);
    color: var(--text-primary);

    svg { transform: translateX(3px); }
  }
`;

const StrategyCard = ({ strategyPoints = [] }) => {
  const navigate = useNavigate();
  return (
    <Panel>
      <Header>
        <Title>
          <FaBullseye /> Estrategia de diferenciación
        </Title>
      </Header>

      {strategyPoints.length > 0 ? (
        <List>
          {strategyPoints.map((p, i) => (
            <Item key={i}>{p}</Item>
          ))}
        </List>
      ) : (
        <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          La narrativa contra los rivales, el contra-mensaje y el plan de acción del día
          se arman en la <b>Sala de Estrategia</b>, con datos en vivo.
        </p>
      )}

      <CtaButton type="button" onClick={() => navigate('/estrategia')}>
        Abrir Sala de Estrategia <FaArrowRight />
      </CtaButton>
    </Panel>
  );
};

export default StrategyCard;
